import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ConnectableObservable, empty, Observable } from "rxjs";
import { publishLast, tap } from "rxjs/operators";

import { withSpinner } from "../../extensions/withSpinner.extension";
import { API_ROOT } from "../../types/constants/appconstants";
import { Community } from "../../types/organization";

@Injectable({
    "providedIn": "root"
})
export class CommunityService {
    private http = inject(HttpClient);
    private apiRoot = inject(API_ROOT);
    private communityServiceBaseUrl = this.apiRoot + "api/communities";

    private lastMarketId: number = null;
    private lastMarketIds: number[] = [];
    private lastMarketId_getCommunities: number;
    private lastMarketId_getCommunitiesOnlyActiveCommunities: number
    private lastMarketId_getCommunitiesOnlyThoseWithPurchaseOrders: number;
    private lastMarketId_getUnclaimedPOReportCommunities: number;
    private lastOrgId: number;
    private communityObs: ConnectableObservable<Community>;
    private communitiesForPurchaseOrdersObs: ConnectableObservable<Community[]>;
    private communitiesObs: ConnectableObservable<Community[]>;
    private communitiesOnlyActiveCommunitiesObs: ConnectableObservable<Community[]>;
    private communitiesOnlyWithPOsObs: ConnectableObservable<Community[]>;
    private marketObs: Observable<any[]>;
    private unclaimedPOReportCommunitiesObs: ConnectableObservable<Community[]>;

    areArraysEqaul(arr1: number[], arr2: number[]): Boolean {
        let isEqual: Boolean = false;

        isEqual = (arr1.length === arr2.length) && arr1.every(function (element, index) {
            return element === arr2[index];
        });

        return isEqual;
    }

    getActiveCommunitiesForPurchaseOrders(marketId: number, refresh: boolean = false): Observable<Community[]> {
        if (refresh || this.lastMarketId_getCommunitiesOnlyActiveCommunities !== marketId) {
            this.communitiesOnlyActiveCommunitiesObs = withSpinner(this.http, "Loading Communities...").post<Community[]>(this.communityServiceBaseUrl + "/communitiesForPurchaseOrders", { marketId: marketId, onlyActiveCommunities: true, includeAccountingUnits: true }).pipe(
                publishLast()
            ) as ConnectableObservable<Community[]>;
            this.communitiesOnlyActiveCommunitiesObs.connect();

            this.lastMarketId_getCommunitiesOnlyActiveCommunities = marketId;
        }

        return this.communitiesOnlyActiveCommunitiesObs;
    }

    getCommunities(marketIds: number[]): Observable<Community[]> {
        // GET /markets/{marketId}/communities
        if (this.lastMarketIds.length === 0 || !this.areArraysEqaul(marketIds, this.lastMarketIds)) {
            this.communitiesObs = withSpinner(this.http).get<Community[]>(this.communityServiceBaseUrl + "?marketIds=[" + marketIds.join(",") + "]").pipe(
                publishLast()
            ) as ConnectableObservable<Community[]>;
            this.communitiesObs.connect();

            this.lastMarketIds = marketIds;
        }

        return this.communitiesObs;
    }

    getCommunitiesForAddTask(marketId: number, masterTaskId: number, taskCategoryId: number, masterTaskIdForSearch?: number): Observable<Community[]> {
        var uriString = `${this.communityServiceBaseUrl}/markets/${marketId}/communities/communitiesforaddtask/${masterTaskId}/${taskCategoryId}`;
        if(masterTaskIdForSearch)
            uriString += `/${masterTaskIdForSearch}`;
        
        return withSpinner(this.http).get<Community[]>(uriString);
    }

    getCommunitiesForDeleteTask(marketId: number, masterTaskId: number, taskCategoryId: number): Observable<Community[]> {
        return withSpinner(this.http).get<Community[]>(`${this.communityServiceBaseUrl}/markets/${marketId}/communities/communitiesfordeletetask/${masterTaskId}/${taskCategoryId}`);
    }

    getCommunitiesForFindAndReplace(marketId: number, masterTaskId: number, replaceMasterTaskId: number, taskCategoryId: number): Observable<any[]> {
        return withSpinner(this.http).get<any[]>(`${this.communityServiceBaseUrl}/markets/${marketId}/communities/templates/task/${masterTaskId}/${replaceMasterTaskId}/${taskCategoryId}`);
    }

    getCommunitiesForPurchaseOrders(marketId: number, onlyThoseWithPurchaseOrders: boolean = false): Observable<Community[]> {
        if (onlyThoseWithPurchaseOrders) {
            if (this.lastMarketId_getCommunitiesOnlyThoseWithPurchaseOrders !== marketId) {
                this.communitiesOnlyWithPOsObs = withSpinner(this.http, "Loading Communities...").post<Community[]>(this.communityServiceBaseUrl + "/communitiesForPurchaseOrders", { marketId: marketId, onlyThoseWithPurchaseOrders: onlyThoseWithPurchaseOrders }).pipe(
                    publishLast()
                ) as ConnectableObservable<Community[]>;
                this.communitiesOnlyWithPOsObs.connect();

                this.lastMarketId_getCommunitiesOnlyThoseWithPurchaseOrders = marketId;
            }

            return this.communitiesOnlyWithPOsObs;
        } else {
            if (this.lastMarketId_getCommunities !== marketId) {
                this.communitiesForPurchaseOrdersObs = withSpinner(this.http, "Loading Communities...").post<Community[]>(this.communityServiceBaseUrl + "/communitiesForPurchaseOrders", { marketId: marketId }).pipe(
                    publishLast()
                ) as ConnectableObservable<Community[]>;
                this.communitiesForPurchaseOrdersObs.connect();

                this.lastMarketId_getCommunities = marketId;
            }

            return this.communitiesForPurchaseOrdersObs;
        }
    }

    getCommunitiesWithPlansAndTemplates(marketId: number): Observable<Community[]> {
        return withSpinner(this.http).get<Community[]>(`${this.communityServiceBaseUrl}/markets/${marketId}/communities/communitieswithplansandtemplates`);
    }

    getCommunityById(orgId: number): Observable<Community> {
        // GET /communities/{orgId}
        if (this.lastOrgId !== orgId) {
            this.communityObs = withSpinner(this.http).get<Community>(`${this.communityServiceBaseUrl}/${orgId}`).pipe(
                publishLast()
            ) as ConnectableObservable<Community>;
            this.communityObs.connect();

            this.lastOrgId = orgId;
        }

        return this.communityObs;
    }

    getCommunitiesWithLotContacts(marketId: number): Observable<Community[]> {
        return withSpinner(this.http).get<Community[]>(`${this.communityServiceBaseUrl}/${marketId}/communitieswithlotcontacts`);
    }

    getConstructionActiveCommunities(marketId: number): Observable<Community[]> {
        return withSpinner(this.http).get<Community[]>(`${this.communityServiceBaseUrl}/${marketId}/constructionactivecommunities`);
    }

    getDivisionTemplateSummary(marketId: number): Observable<any[]> {
        if (this.lastMarketId === null || this.lastMarketId !== marketId) {
            this.lastMarketId = marketId;
            const marketObs = withSpinner(this.http).get<any[]>(`${this.communityServiceBaseUrl}/markets/${marketId}/communitiestemplates`).pipe(
                publishLast()
            ) as ConnectableObservable<any[]>;
            marketObs.connect();
            this.marketObs = marketObs;
        }

        return this.marketObs;
    }

    getLotFieldContactCommunityIds(marketId: number): Observable<number[]> {
        return withSpinner(this.http).get<number[]>(`${this.communityServiceBaseUrl}/${marketId}/lotfieldcontactcommunityids`);
    }

    getMySchedule(marketId: number, azureGUID: string): Observable<Community[]> {
        return withSpinner(this.http).get<Community[]>(`${this.communityServiceBaseUrl}/myschedules/${marketId}/${azureGUID}`);
    }

    getUnclaimedPOReportCommunities(marketId: number): Observable<Community[]> {
        if (this.lastMarketId_getUnclaimedPOReportCommunities !== marketId) {
            this.unclaimedPOReportCommunitiesObs = withSpinner(this.http).get<Community[]>(this.communityServiceBaseUrl + "/unclaimedPOReportCommunities/" + marketId).pipe(
                publishLast()
            ) as ConnectableObservable<Community[]>;
            this.unclaimedPOReportCommunitiesObs.connect();

            this.lastMarketId_getUnclaimedPOReportCommunities = marketId;
        }

        return this.unclaimedPOReportCommunitiesObs;
    }

    saveCommunities(communities: Community[], marketId: number): Observable<any> {
        return withSpinner(this.http).post(this.communityServiceBaseUrl, { "communities": communities, "marketId": marketId }).pipe(
            tap((community) => this.refreshCommunities(marketId))
        );
    }

    updateCommunityPoStatus(communities: Community[]): Observable<Community[]> {
        this.lastMarketId_getCommunitiesOnlyThoseWithPurchaseOrders = null;
        this.lastMarketId_getCommunities = null;
        return withSpinner(this.http, "Saving Communities...").patch<Community[]>(this.communityServiceBaseUrl, { communities: communities });
    }

    private refreshCommunities(marketId: number) {
        const marketIds: number[] = [marketId];

        this.communitiesObs = withSpinner(this.http).get<Community[]>(this.communityServiceBaseUrl + "?marketIds=[" + marketIds.join(",") + "]").pipe(
            publishLast()
        ) as ConnectableObservable<Community[]>;
        this.communitiesObs.connect();

        this.lastMarketIds = marketIds;
    }
}